import { Quote } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import ScrollReveal from "@/components/ScrollReveal";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { useRef } from "react";

interface ClientTestimonial {
  name: string;
  contact_name: string | null;
  testimonial: string;
}

const Testimonial = () => {
  const { t } = useTranslation();
  const autoplayPlugin = useRef(
    Autoplay({ delay: 6000, stopOnInteraction: true })
  );

  // Check if testimonials section should be shown
  const { data: showTestimonials } = useQuery({
    queryKey: ["site-settings", "show_testimonials"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "show_testimonials")
        .maybeSingle();
      if (error) throw error;
      // Default to true if setting doesn't exist
      return data?.value !== false;
    },
  });

  const { data: testimonials, isLoading } = useQuery({
    queryKey: ["client-testimonials"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("clients")
        .select("name, contact_name, testimonial")
        .eq("is_active", true)
        .not("testimonial", "is", null)
        .neq("testimonial", "")
        .order("display_order", { ascending: true });

      if (error) throw error;
      return data as ClientTestimonial[];
    },
    enabled: showTestimonials !== false,
  });

  // Hide entire section if setting is false
  if (showTestimonials === false) {
    return null;
  }

  // Fallback testimonial from translations
  const fallbackTestimonial = {
    quote: t("testimonial.quote"),
    author: t("testimonial.author"),
    role: t("testimonial.role"),
  };

  // If no testimonials from DB or still loading, show fallback
  if (isLoading || !testimonials || testimonials.length === 0) {
    return (
      <section className="section-padding bg-background">
        <div className="container-wide px-5 sm:px-6">
          <ScrollReveal animation="scale" className="max-w-3xl mx-auto text-center">
            <Quote size={32} className="sm:w-10 sm:h-10 mx-auto mb-6 sm:mb-8 text-primary/40" strokeWidth={1} />
            
            <blockquote className="font-display text-lg sm:text-2xl md:text-3xl lg:text-4xl font-normal leading-relaxed mb-6 sm:mb-8 text-primary">
              "{fallbackTestimonial.quote}"
            </blockquote>
            
            <div className="flex flex-col items-center">
              <p className="font-medium text-primary text-sm sm:text-base">{fallbackTestimonial.author}</p>
              <p className="text-xs sm:text-sm text-muted-foreground">{fallbackTestimonial.role}</p>
            </div>
          </ScrollReveal>
        </div>
      </section>
    );
  }

  // Single testimonial - no carousel needed
  if (testimonials.length === 1) {
    const testimonial = testimonials[0];
    return (
      <section className="section-padding bg-background">
        <div className="container-wide px-5 sm:px-6">
          <ScrollReveal animation="scale" className="max-w-3xl mx-auto text-center">
            <Quote size={32} className="mx-auto mb-6 sm:mb-8 text-primary/40" strokeWidth={1} />
            
            <blockquote className="font-display text-lg sm:text-2xl md:text-3xl lg:text-4xl font-normal leading-relaxed mb-6 sm:mb-8 text-primary">
              "{testimonial.testimonial}"
            </blockquote>
            
            <div className="flex flex-col items-center">
              {testimonial.contact_name && (
                <p className="font-medium text-primary text-sm sm:text-base">{testimonial.contact_name}</p>
              )}
              <p className="text-xs sm:text-sm text-muted-foreground">{testimonial.name}</p>
            </div>
          </ScrollReveal>
        </div>
      </section>
    );
  }

  // Multiple testimonials - use carousel
  return (
    <section className="section-padding bg-background">
      <div className="container-wide">
        <ScrollReveal animation="scale" className="max-w-4xl mx-auto">
          <Carousel
            opts={{
              align: "center",
              loop: true,
            }}
            plugins={[autoplayPlugin.current]}
            className="w-full"
          >
            <CarouselContent>
              {testimonials.map((testimonial, index) => (
                <CarouselItem key={index} className="flex items-center justify-center">
                  <div className="text-center max-w-3xl mx-auto px-4 sm:px-8 md:px-4">
                    <Quote size={32} className="mx-auto mb-6 sm:mb-8 text-primary/40" strokeWidth={1} />
                    
                    <blockquote className="font-display text-lg sm:text-2xl md:text-3xl lg:text-4xl font-normal leading-relaxed mb-6 sm:mb-8 text-primary">
                      "{testimonial.testimonial}"
                    </blockquote>
                    
                    <div className="flex flex-col items-center">
                      {testimonial.contact_name && (
                        <p className="font-medium text-primary text-sm sm:text-base">{testimonial.contact_name}</p>
                      )}
                      <p className="text-xs sm:text-sm text-muted-foreground">{testimonial.name}</p>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="-left-2 md:-left-12 w-10 h-10 border-primary text-primary hover:bg-primary hover:text-primary-foreground" />
            <CarouselNext className="-right-2 md:-right-12 w-10 h-10 border-primary text-primary hover:bg-primary hover:text-primary-foreground" />
          </Carousel>
          
          {/* Dots indicator */}
          <div className="flex justify-center gap-2 mt-8">
            {testimonials.map((_, index) => (
              <div
                key={index}
                className="w-2 h-2 rounded-full bg-primary/30"
              />
            ))}
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default Testimonial;